<template>
    <div class="allocations-by-industry-table">
        <b-table
            :data="processedIndustries"
            :mobile-cards="false"
        >
            <template slot-scope="props">
                <b-table-column
                    label="Industry"
                    field="rowName"
                >
                    {{ props.row.rowName }}
                </b-table-column>
                <b-table-column
                    label="Annual Volume"
                    centered
                    width="100%"
                >
                    <div
                        :id="props.row.id"
                        class="volume-bar"
                    >
                        <div
                            class="bar-section licence-color sw-long"
                            :style="{ width: `${props.row.percentages.sw_long}%` }"
                        />
                        <div
                            class="bar-section licence-color sw-short"
                            :style="{ width: `${props.row.percentages.sw_short}%` }"
                        />
                        <div
                            class="bar-section licence-color gw-long"
                            :style="{ width: `${props.row.percentages.gw_long}%` }"
                        />
                        <div
                            class="bar-section licence-color gw-short"
                            :style="{ width: `${props.row.percentages.gw_short}%` }"
                        />
                    </div>
                </b-table-column>
                <b-table-column
                    field="groundwater"
                    label="Ground Water (m³)"
                    width="16em"
                >
                    <template
                        slot="header"
                        slot-scope="{ column }"
                    >
                        <div class="header-container">
                            <div class="top-header">
                                {{ column.label }}
                            </div>
                            <div class="secondary-header-container">
                                <div class="secondary-header">
                                    <b-tooltip
                                        :label="subHeaders.shortTooltip"
                                        type="is-light"
                                    >
                                        {{ subHeaders.short }}
                                    </b-tooltip>
                                    <div
                                        class="color-box licence-color gw-short"
                                    />
                                </div>
                                <div class="secondary-header">
                                    <b-tooltip
                                        :label="subHeaders.longTooltip"
                                        type="is-light"
                                    >
                                        {{ subHeaders.long }}
                                    </b-tooltip>
                                    <div
                                        class="color-box licence-color gw-long"
                                    />
                                </div>
                            </div>
                        </div>
                    </template>
                    <div class="water-type groundwater">
                        <div class="term-type">
                            <span :class="{'is-hidden-touch': !pdf}">
                                {{ props.row.gw_short | numFormat }}
                            </span>
                            <span
                                v-if="!pdf"
                                class="is-hidden-desktop"
                            >
                                {{ props.row.gw_short | shortenLargeNum }}
                            </span>
                        </div>
                        <div class="term-type">
                            <span :class="{'is-hidden-touch': !pdf}">
                                {{ props.row.gw_long | numFormat }}
                            </span>
                            <span
                                v-if="!pdf"
                                class="is-hidden-desktop"
                            >
                                {{ props.row.gw_long | shortenLargeNum }}
                            </span>
                        </div>
                    </div>
                </b-table-column>

                <b-table-column
                    field="surface-water"
                    label="Surface Water (m³)"
                    width="16em"
                >
                    <template
                        slot="header"
                        slot-scope="{ column }"
                    >
                        <div class="header-container">
                            <div class="top-header">
                                {{ column.label }}
                            </div>
                            <div class="secondary-header-container">
                                <div class="secondary-header">
                                    <b-tooltip
                                        :label="subHeaders.shortTooltip"
                                        type="is-light"
                                    >
                                        {{ subHeaders.short }}
                                    </b-tooltip>
                                    <div
                                        class="color-box licence-color sw-short"
                                    />
                                </div>
                                <div class="secondary-header">
                                    <b-tooltip
                                        :label="subHeaders.longTooltip"
                                        type="is-light"
                                    >
                                        {{ subHeaders.long }}
                                    </b-tooltip>
                                    <div
                                        class="color-box licence-color sw-long"
                                    />
                                </div>
                            </div>
                        </div>
                    </template>
                    <div class="water-type groundwater">
                        <div class="term-type">
                            <span :class="{'is-hidden-touch': !pdf}">
                                {{ props.row.sw_short | numFormat }}
                            </span>
                            <span
                                v-if="!pdf"
                                class="is-hidden-desktop"
                            >
                                {{ props.row.sw_short | shortenLargeNum }}
                            </span>
                        </div>
                        <div class="term-type">
                            <span :class="{'is-hidden-touch': !pdf}">
                                {{ props.row.sw_long | numFormat }}
                            </span>
                            <span
                                v-if="!pdf"
                                class="is-hidden-desktop"
                            >
                                {{ props.row.sw_long | shortenLargeNum }}
                            </span>
                        </div>
                    </div>
                </b-table-column>
            </template>
        </b-table>
    </div>
</template>

<script>
export default {
    name: 'AllocationsByIndustryTable',

    props: {
        industries: {
            type: Object,
            required: true,
        },
        subHeaders: {
            type: Object,
            required: true,
        },
        pdf: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    data() {
        return {
            resizeObserver: null,
            annualVolumeColumn: true,
            processedIndustries: [],
        };
    },

    beforeMount() {
        const tableData = [];
        Object.keys(this.industries).forEach((key) => {
            const row = this.industries[key];
            row.rowName = key;
            tableData.push(row);
        });

        this.processedIndustries = tableData;
        this.setPercentages();
    },

    beforeDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },

    methods: {
        setPercentages() {
            const industries = this.processedIndustries;
            const rowTotals = Object.values(industries).map((d) => d.sw_long + d.sw_short + d.gw_long + d.gw_short);
            const domain = Math.max(...rowTotals);

            Object.keys(industries).forEach((key) => {
                const industry = industries[key];

                const percentages = {
                    sw_long: this.calculatePercentage(industry.sw_long, domain),
                    sw_short: this.calculatePercentage(industry.sw_short, domain),
                    gw_long: this.calculatePercentage(industry.gw_long, domain),
                    gw_short: this.calculatePercentage(industry.gw_short, domain),
                };

                this.processedIndustries[key].percentages = percentages;
            });
        },

        calculatePercentage(num, total) {
            return ((num / total) * 100).toFixed(3);
        },
    },
};
</script>

<style lang="scss">
.allocations-by-industry-table {
    $desktop: 1024px !default;
    $tablet: 769px !default;
    $widescreen: 1216px !default;
    $color-allocations-sw-long: #002d73 !default;
    $color-allocations-sw-short: #f7a800 !default;
    $color-allocations-gw-long: #29b6f6 !default;
    $color-allocations-gw-short: #ab47bc !default;
    $licence-column-width: 8em;

    padding: 0 0.6em;

    @media screen and (max-width: $desktop) {
        font-size: 12px;
    }

    @media screen and (max-width: $tablet) {
        font-size: 12px;
        padding: 0;
    }

    @media screen and (max-width: $widescreen) {
        th,
        td {
            // 'Annual Volume' column
            &:nth-child(2) {
                display: none;
            }
        }
    }

    th {
        // 'Ground Water' and 'Surface Water' columns
        &:nth-child(3),
        &:nth-child(4) {
            .th-wrap {
                > * {
                    flex: 1;
                }
            }
        }

        .th-wrap .icon {
            display: none;
        }

        .header-container {
            width: fit-content;
            float: right;
            display: flex;
            flex-direction: column;

            .top-header {
                align-self: center;
            }
        }
    }

    td {
        vertical-align: middle;
    }

    // stylelint-disable-next-line no-descending-specificity
    th,
    td {
        // 'Ground Water' column
        &:nth-child(3) {
            padding-right: 0;
        }

        // 'Surface Water' column
        &:nth-child(4) {
            padding-left: 0;
        }

        .secondary-header-container,
        .water-type {
            flex: 1;
            display: flex;
            flex-direction: row;
            justify-content: flex-end;

            .secondary-header,
            .term-type {
                display: flex;
                align-items: center;
                justify-content: flex-end;
            }

            // we only want to apply the width to the long term header. This is so that the short term header's width
            // will just fit its content. By doing this, the header container's width will just be wide enough to
            // encompass the secondary headers, and the top header can be perfectly centered between the two secondary
            // headers
            .secondary-header {
                // long term secondary header
                &:nth-child(2) {
                    width: $licence-column-width;
                }

                font-weight: normal;
            }

            .term-type {
                width: $licence-column-width;
            }
        }
    }

    // hide bottom border of table
    tr:last-child td {
        border-bottom: none;
    }

    .color-box {
        width: 1em;
        height: 1em;
        border: 1px solid black;
        border-radius: 2px;
        margin-left: 0.4em;
    }

    .volume-bar {
        width: 100%;
        height: 25px;
        display: flex;
        flex-direction: row;
        margin: 5px;
        margin-right: 0;
    }

    .licence-color {
        &.sw-long {
            background-color: $color-allocations-sw-long;
        }

        &.sw-short {
            background-color: $color-allocations-sw-short;
        }

        &.gw-long {
            background-color: $color-allocations-gw-long;
        }

        &.gw-short {
            background-color: $color-allocations-gw-short;
        }
    }
}

.static-report .allocations-by-industry-table {
    padding: 0;
    font-size: 10px;

    th {
        font-size: 11px;

        .secondary-header {
            font-size: 10px;

            span.b-tooltip {
                margin-bottom: -1px;
            }
        }
    }

    th,
    td {
        // 'Annual Volume' column
        &:nth-child(2) {
            display: table-cell;

            .volume-bar {
                height: 10px;
                margin: 0 5px;
            }
        }
    }
}
</style>
